import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useIsPortal } from 'utils/Subscription/PermissionSelector'
import ConfirmModalLinkVehicle from 'app/vehicles/components/LinkVehicleToWorkorder/ConfirmModalLinkVehicle'
import ConfirmModalLinkAccount from 'app/vehicles/components/LinkVehicleToWorkorder/ConfirmModalLinkAccount'
import { generateApiError } from 'utils/Request/Error'
import { IAccount, LinkVehicleToWorkorderResolvingAction, LinkVehicleToWorkorderResolvingActionType } from '@cbgms/api'
import { useNotifier } from '@cbgms/base/notifier'
import { getShowConfirmModalLinkAccount, getShowConfirmModalLinkVehicle, getVehicleLinkReceivedData } from 'app/vehicles/state/selectors'
import { clearVehicleLinkModalState, setVehicleLinkModalState, setVehicleLinkReceivedData } from 'app/vehicles/state/actions'
import { linkVehicleToWorkorder } from 'app/workorder/state/workorder/actions'
import { useWorkorderContext } from 'contexts/WorkorderContext/use-workorder-context'
import { getOrderWithRelated } from 'app/order/state/actions'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { useWorkorderBaseRoute } from 'utils/Subscription/pathAndRouteSelector'

/**
 * Component that handles rendering of vehicle linking modals.
 * Uses Redux directly for modal state management to avoid hook instance issues.
 */
export const VehicleLinkingModals: React.FC = () => {
  const isPortal = useIsPortal()
  const dispatch = useDispatch()
  const notifier = useNotifier()
  const { t } = useTranslation()
  const { getWorkorderUUID } = useWorkorderContext()
  const navigation = useHistory()
  const baseURL = useWorkorderBaseRoute()

  // Get modal state from Redux
  const showConfirmModalLinkVehicle = useSelector(getShowConfirmModalLinkVehicle)
  const showConfirmModalLinkAccount = useSelector(getShowConfirmModalLinkAccount)
  const receivedData = useSelector(getVehicleLinkReceivedData)

  // Vehicle link success callback
  const onVehicleLinkSuccess = useCallback(async () => {
    try {
      const workorderUUID = await getWorkorderUUID({})
      await dispatch(getOrderWithRelated(workorderUUID))
      notifier.success(t('workorder.vehicle_linked'))
      // Navigate to workorder page after successful vehicle linking
      navigation.push(`${baseURL}${workorderUUID}`)
    } catch (e) {
      const apiError = generateApiError(e)
      notifier.error(apiError.error)
    }
  }, [dispatch, getWorkorderUUID, notifier, t, navigation, baseURL])

  // Vehicle linking function
  const linkVehicle = useCallback(
    async (vehicleUUID: string, actions: LinkVehicleToWorkorderResolvingAction[] = []) => {
      try {
        const workorderUUID = await getWorkorderUUID({})
        const { Data } = await dispatch(
          linkVehicleToWorkorder(workorderUUID, {
            VehicleUUID: vehicleUUID,
            Actions: actions
          })
        )

        dispatch(setVehicleLinkReceivedData(Data))

        if (Data.Rejections?.length) {
          dispatch(setVehicleLinkModalState('vehicle', true))
        } else if (Data.Warnings?.length) {
          dispatch(setVehicleLinkModalState('account', true))
        } else {
          dispatch(clearVehicleLinkModalState())
          await onVehicleLinkSuccess()
        }
      } catch (e) {
        const apiError = generateApiError(e)
        notifier.error(apiError.error)
      }
    },
    [dispatch, getWorkorderUUID, notifier, onVehicleLinkSuccess]
  )

  // Modal logic functions
  const getVehicleAccountFromWarnings = useCallback((): IAccount => {
    if (receivedData?.Warnings?.[0]) {
      return receivedData.Warnings[0].Data.VehicleAccount
    }
    return {} as IAccount
  }, [receivedData])

  const onConfirmLinkAccount = useCallback(
    async (doUpdateVehicle = false) => {
      try {
        dispatch(setVehicleLinkModalState('vehicle', false))
        dispatch(setVehicleLinkModalState('account', false))

        if (doUpdateVehicle && receivedData?.Vehicle?.UUID) {
          await linkVehicle(receivedData.Vehicle.UUID, [
            {
              ActionType: LinkVehicleToWorkorderResolvingActionType.IgnoreRelatedWorkorders
            },
            {
              ActionType: LinkVehicleToWorkorderResolvingActionType.LinkWorkorderAccountToVehicle
            }
          ])
        }

        await onVehicleLinkSuccess()
      } catch (e) {
        const apiError = generateApiError(e)
        notifier.error(apiError.error)
      }
    },
    [dispatch, receivedData, linkVehicle, onVehicleLinkSuccess, notifier]
  )

  const handleLinkVehicle = useCallback(
    async (vehicleUUID: string, actions: LinkVehicleToWorkorderResolvingAction[] = []) => {
      try {
        await linkVehicle(vehicleUUID, actions)
      } catch (e) {
        const apiError = generateApiError(e)
        notifier.error(apiError.error)
      }
    },
    [linkVehicle, notifier]
  )

  // Only render in portal mode
  if (!isPortal) {
    return null
  }

  return (
    <>
      {/* Vehicle Link Modal */}
      {showConfirmModalLinkVehicle && receivedData && (
        <ConfirmModalLinkVehicle
          open={showConfirmModalLinkVehicle}
          onClose={() => dispatch(setVehicleLinkModalState('vehicle', false))}
          onConfirm={actions => handleLinkVehicle(receivedData.Vehicle.UUID, actions)}
          rejections={receivedData.Rejections || []}
        />
      )}

      {/* Account Link Modal */}
      {showConfirmModalLinkAccount && receivedData && (
        <ConfirmModalLinkAccount
          open={showConfirmModalLinkAccount}
          currentVehicleAccount={getVehicleAccountFromWarnings()}
          onConfirm={() => onConfirmLinkAccount(true)}
          onClose={() => onConfirmLinkAccount(false)}
        />
      )}
    </>
  )
}
